<template>
  <div v-if="open" class="overlay" @click.self="$emit('close')">
    <div class="modal">
      <div class="modal-head">
        <div>
          <div class="eyebrow">Sincronización</div>
          <h3>Inicia sesión para guardar y colaborar</h3>
        </div>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <div class="tabs">
        <button :class="{ active: mode === 'login' }" @click="mode = 'login'">Entrar</button>
        <button :class="{ active: mode === 'register' }" @click="mode = 'register'">Crear cuenta</button>
      </div>

      <form class="form" @submit.prevent="submit">
        <template v-if="mode === 'register'">
          <input v-model.trim="form.nombre" type="text" placeholder="Nombre completo" required>
          <input v-model.trim="form.username" type="text" placeholder="Usuario" required>
          <input v-model.trim="form.email" type="email" placeholder="Correo" required>
          <input v-model="form.password" type="password" placeholder="Contraseña" required>

          <label class="legal-check">
            <input v-model="acceptedLegal" type="checkbox" required>
            <span>
              Leí y acepto los
              <button type="button" class="inline-link" @click="openLegal('terms')">Términos y condiciones</button>
              y el
              <button type="button" class="inline-link" @click="openLegal('privacy')">Aviso de privacidad</button>.
            </span>
          </label>
        </template>

        <template v-else>
          <input v-model.trim="form.email_or_username" type="text" placeholder="Correo o usuario" required>
          <input v-model="form.password" type="password" placeholder="Contraseña" required>
        </template>

        <button class="submit-btn" :disabled="loading">
          {{ loading ? 'Procesando...' : (mode === 'login' ? 'Entrar' : 'Crear cuenta') }}
        </button>
      </form>

      <div class="legal-shortcuts">
        <span>Consulta</span>
        <button type="button" class="inline-link" @click="openLegal('terms')">Términos y condiciones</button>
        <span>y</span>
        <button type="button" class="inline-link" @click="openLegal('privacy')">Aviso de privacidad</button>
      </div>

      <p v-if="error" class="error">{{ error }}</p>
    </div>

    <div v-if="legalOpen" class="legal-overlay" @click.self="closeLegal">
      <div class="legal-modal">
        <div class="legal-modal-head">
          <div>
            <div class="eyebrow">Información legal</div>
            <h4>{{ activeLegal.title }}</h4>
            <p>{{ activeLegal.updatedAt }}</p>
          </div>
          <button class="close-btn legal-close" type="button" @click="closeLegal">×</button>
        </div>

        <div class="legal-switcher">
          <button type="button" :class="{ active: legalDoc === 'terms' }" @click="legalDoc = 'terms'">Términos</button>
          <button type="button" :class="{ active: legalDoc === 'privacy' }" @click="legalDoc = 'privacy'">Privacidad</button>
        </div>

        <div class="legal-body">
          <section v-for="section in activeLegal.sections" :key="section.heading" class="legal-section">
            <h5>{{ section.heading }}</h5>
            <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
          </section>
        </div>

        <div class="legal-footer">
          <a href="https://fundatusitioweb.com" target="_blank" rel="noopener noreferrer">fundatusitioweb.com</a>
          <a href="mailto:gabriela.martinez@fundatusitioweb.com">gabriela.martinez@fundatusitioweb.com</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { legalDocuments } from '@/constants/legal-documents';

export default {
  name: 'AuthModal',
  props: {
    open: { type: Boolean, default: false },
    loginAction: { type: Function, required: true },
    registerAction: { type: Function, required: true },
  },
  emits: ['close'],
  data() {
    return {
      mode: 'login',
      loading: false,
      error: '',
      acceptedLegal: false,
      legalOpen: false,
      legalDoc: 'terms',
      legalDocuments,
      form: {
        nombre: '',
        username: '',
        email: '',
        password: '',
        email_or_username: '',
      },
    };
  },
  computed: {
    activeLegal() {
      return this.legalDocuments[this.legalDoc] || this.legalDocuments.terms;
    },
  },
  watch: {
    open(value) {
      if (!value) {
        this.legalOpen = false;
      }
    },
  },
  methods: {
    openLegal(doc) {
      this.legalDoc = doc;
      this.legalOpen = true;
    },
    closeLegal() {
      this.legalOpen = false;
    },
    async submit() {
      this.loading = true;
      this.error = '';
      try {
        if (this.mode === 'login') {
          await this.loginAction({
            email_or_username: this.form.email_or_username,
            password: this.form.password,
          });
        } else {
          if (!this.acceptedLegal) {
            throw new Error('Necesitas aceptar los términos y el aviso de privacidad para crear tu cuenta.');
          }
          await this.registerAction({
            nombre: this.form.nombre,
            username: this.form.username,
            email: this.form.email,
            password: this.form.password,
          });
        }
      } catch (error) {
        this.error = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'No se pudo completar la acción.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.34);
  display: grid;
  place-items: center;
  padding: 24px;
  z-index: 40;
}
.modal {
  width: min(520px, 100%);
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 24px;
  padding: 22px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.18);
}
.modal-head,
.legal-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.eyebrow {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #64748b;
}
h3 {
  margin: 5px 0 0;
  font-size: 28px;
  color: #0f172a;
}
h4 {
  margin: 5px 0 0;
  font-size: 24px;
  color: #0f172a;
}
.legal-modal-head p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}
.close-btn {
  border: none;
  background: transparent;
  font-size: 34px;
  color: #64748b;
  cursor: pointer;
}
.tabs,
.legal-switcher {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}
.tabs button,
.legal-switcher button {
  border: none;
  border-radius: 999px;
  padding: 10px 14px;
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
  cursor: pointer;
  font-weight: 700;
}
.tabs .active,
.legal-switcher .active {
  background: #111827;
  color: white;
}
.form {
  display: grid;
  gap: 12px;
  margin-top: 18px;
}
.form input {
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(248,250,252,0.92);
  padding: 14px 16px;
  font-size: 16px;
  outline: none;
}
.legal-check {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(248,250,252,0.92);
  border: 1px solid rgba(148,163,184,.18);
  color: #334155;
  font-size: 14px;
  line-height: 1.5;
}
.legal-check input {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  padding: 0;
}
.inline-link {
  border: none;
  background: transparent;
  color: #1d4ed8;
  font-weight: 800;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.submit-btn {
  border: none;
  border-radius: 16px;
  background: #111827;
  color: white;
  padding: 14px 18px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
}
.legal-shortcuts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
  color: #64748b;
  font-size: 14px;
}
.error {
  margin: 12px 0 0;
  color: #b43737;
}
.legal-overlay {
  position: fixed;
  inset: 0;
  z-index: 55;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(10px);
}
.legal-modal {
  width: min(760px, 100%);
  max-height: min(88vh, 820px);
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  gap: 14px;
  padding: 22px;
  border-radius: 26px;
  background: rgba(255,255,255,.98);
  border: 1px solid rgba(148,163,184,.22);
  box-shadow: 0 28px 80px rgba(15,23,42,.22);
}
.legal-close {
  line-height: 1;
}
.legal-body {
  overflow: auto;
  padding-right: 4px;
  display: grid;
  gap: 14px;
}
.legal-section {
  display: grid;
  gap: 8px;
  padding: 0 2px;
}
.legal-section h5 {
  margin: 0;
  font-size: 16px;
  color: #0f172a;
}
.legal-section p {
  margin: 0;
  color: #475569;
  line-height: 1.65;
}
.legal-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(226,232,240,.9);
}
.legal-footer a {
  color: #1d4ed8;
  font-weight: 700;
  text-decoration: none;
}
@media (max-width: 640px) {
  .overlay,
  .legal-overlay {
    padding: 14px;
  }
  .modal,
  .legal-modal {
    padding: 18px;
    border-radius: 22px;
  }
  h3 {
    font-size: 24px;
  }
  h4 {
    font-size: 22px;
  }
}
</style>
